import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Shield, Search, Download, Trash2, AlertTriangle, FileText, Clock, Users, Power, Database } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

export default function GDPRCompliance() {
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [email, setEmail] = useState("");
  const [exportData, setExportData] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  // Duplicate-account audit state
  const [dupData, setDupData] = useState(null);
  const [dupLoading, setDupLoading] = useState(false);
  const [dedupeConfirm, setDedupeConfirm] = useState(null); // { kind, record_id, name, balance }
  const [dedupeReason, setDedupeReason] = useState("");
  const [dedupeForfeit, setDedupeForfeit] = useState(false);
  const { authAxios } = useAuth();

  useEffect(() => {
    authAxios.get("/platform/tenants").then(r => setTenants(r.data)).catch(err => console.error("Request failed:", err?.message || err));
    authAxios.get("/platform/gdpr-audit").then(r => setAuditLogs(r.data)).catch(err => console.error("Request failed:", err?.message || err));
  }, []);

  const handleExport = async () => {
    if (!selectedTenant || !email) return toast.error("Select tenant and enter email");
    setLoading(true);
    try {
      const res = await authAxios.get(`/platform/tenants/${selectedTenant.id}/gdpr-export/${email}`);
      setExportData(res.data);
      toast.success("Data exported");
    } catch (e) { toast.error(e.response?.data?.detail || "Export failed"); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setLoading(true);
    try {
      const res = await authAxios.delete(`/platform/tenants/${deleteConfirm.tenantId}/gdpr-delete/${deleteConfirm.email}`, { data: { confirm: true } });
      toast.success(res.data.message);
      setDeleteConfirm(null);
      setExportData(null);
      setEmail("");
      // Refresh audit log
      const auditRes = await authAxios.get("/platform/gdpr-audit");
      setAuditLogs(auditRes.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Deletion failed"); }
    finally { setLoading(false); }
  };

  const handleAudit = async () => {
    if (!selectedTenant || !email) return toast.error("Select tenant and enter email");
    setDupLoading(true);
    setDupData(null);
    try {
      const res = await authAxios.get(`/platform/tenants/${selectedTenant.id}/email-audit/${email}`);
      setDupData(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Audit failed"); }
    finally { setDupLoading(false); }
  };

  const handleDedupe = async () => {
    if (!dedupeConfirm) return;
    if (!dedupeReason || dedupeReason.trim().length < 8) return toast.error("Reason must be at least 8 characters");
    setDupLoading(true);
    try {
      const res = await authAxios.post(`/platform/tenants/${selectedTenant.id}/deactivate-duplicate`, {
        kind: dedupeConfirm.kind,
        record_id: dedupeConfirm.record_id,
        reason: dedupeReason.trim(),
        allow_wallet_forfeit: dedupeForfeit,
      });
      toast.success(res.data.message);
      setDedupeConfirm(null);
      setDedupeReason("");
      setDedupeForfeit(false);
      // Refresh audit + row list.
      handleAudit();
      const auditRes = await authAxios.get("/platform/gdpr-audit");
      setAuditLogs(auditRes.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Deactivation failed"); }
    finally { setDupLoading(false); }
  };

  const downloadJson = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  // iter143: Full-tenant data wipe (destructive) — Sys Admin only.
  const [wipeTenantId, setWipeTenantId] = useState("");
  const [wipePreview, setWipePreview] = useState(null); // { tenant, preserved, wiped, totals }
  const [wipeLoading, setWipeLoading] = useState(false);
  const [wipeReason, setWipeReason] = useState("");
  const [wipeSlugConfirm, setWipeSlugConfirm] = useState("");
  const [wipeStage, setWipeStage] = useState("idle"); // idle | preview | confirm

  const handleWipePreview = async () => {
    if (!wipeTenantId) return toast.error("Select a tenant");
    setWipeLoading(true);
    setWipePreview(null);
    try {
      const res = await authAxios.post(`/platform/tenants/${wipeTenantId}/wipe-data-preview`);
      setWipePreview(res.data);
      setWipeStage("preview");
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setWipeLoading(false); }
  };

  const handleWipeExecute = async () => {
    if (!wipePreview) return;
    if (wipeSlugConfirm !== wipePreview.tenant.slug) return toast.error("Slug does not match");
    if (!wipeReason || wipeReason.trim().length < 12) return toast.error("Reason must be >= 12 characters");
    setWipeLoading(true);
    try {
      const res = await authAxios.post(`/platform/tenants/${wipeTenantId}/wipe-data-execute`, {
        confirm_slug: wipeSlugConfirm,
        reason: wipeReason.trim(),
      });
      toast.success(res.data.message);
      setWipeStage("idle");
      setWipePreview(null);
      setWipeSlugConfirm("");
      setWipeReason("");
      const auditRes = await authAxios.get("/platform/gdpr-audit");
      setAuditLogs(auditRes.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Wipe failed"); }
    finally { setWipeLoading(false); }
  };

  return (
    <div data-testid="gdpr-compliance">
      <div className="flex items-center gap-2 mb-4">
        <Shield className="w-5 h-5 text-[#1e3a5f]" />
        <h2 className="text-lg font-bold text-[#1e3a5f]">GDPR Compliance</h2>
      </div>

      {/* Data Subject Request */}
      <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
        <h3 className="font-bold text-sm text-[#1e3a5f] mb-3">Data Subject Request</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Tenant</label>
            <select
              value={selectedTenant?.id || ""}
              onChange={e => setSelectedTenant(tenants.find(t => t.id === e.target.value))}
              className="w-full px-3 py-2.5 rounded-xl border text-sm"
            >
              <option value="">Select tenant</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">User Email</label>
            <input
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="user@example.com"
              type="email"
              className="w-full px-3 py-2.5 rounded-xl border text-sm"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleExport}
              disabled={loading || !selectedTenant || !email}
              className="flex-1 py-2.5 rounded-xl bg-[#1e3a5f] text-white font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="gdpr-export-btn"
            >
              <Download className="w-4 h-4" /> Export Data (Right to Access)
            </button>
            <button
              onClick={() => {
                if (!selectedTenant || !email) return toast.error("Select tenant and enter email");
                setDeleteConfirm({ tenantId: selectedTenant.id, email, tenantName: selectedTenant.name });
              }}
              disabled={loading || !selectedTenant || !email}
              className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="gdpr-delete-btn"
            >
              <Trash2 className="w-4 h-4" /> Delete Data (Right to Erasure)
            </button>
          </div>
          <button
            onClick={handleAudit}
            disabled={dupLoading || !selectedTenant || !email}
            className="w-full mt-2 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="dupe-audit-btn"
          >
            <Users className="w-4 h-4" /> {dupLoading ? "Auditing…" : "Audit duplicate accounts"}
          </button>
        </div>
      </div>

      {/* iter143: Full tenant data wipe — Sys Admin destructive tool */}
      <div className="bg-white rounded-2xl p-4 shadow-sm mb-4 border-2 border-red-100" data-testid="tenant-wipe-panel">
        <div className="flex items-center gap-2 mb-3">
          <Database className="w-4 h-4 text-red-600" />
          <h3 className="font-bold text-sm text-red-700">Full Tenant Data Wipe</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3 leading-snug">
          Deletes every collection in the tenant DB except <code className="bg-slate-100 px-1 rounded">finance_admins</code>, <code className="bg-slate-100 px-1 rounded">member_types</code>, and <code className="bg-slate-100 px-1 rounded">shop_charges_config</code>. Sys Admins live on the platform DB (untouched). This is destructive and cannot be undone.
        </p>
        <div className="flex gap-2 mb-2">
          <select
            value={wipeTenantId}
            onChange={e => { setWipeTenantId(e.target.value); setWipePreview(null); setWipeStage("idle"); }}
            className="flex-1 px-3 py-2 rounded-xl border text-sm"
            data-testid="wipe-tenant-select"
          >
            <option value="">Select tenant</option>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>)}
          </select>
          <button
            onClick={handleWipePreview}
            disabled={wipeLoading || !wipeTenantId}
            className="px-3 py-2 rounded-xl bg-slate-700 text-white text-xs font-bold disabled:opacity-50"
            data-testid="wipe-preview-btn"
          >
            {wipeLoading && wipeStage === "idle" ? "Loading…" : "Preview counts"}
          </button>
        </div>

        {wipePreview && (
          <div className="mt-3 space-y-3" data-testid="wipe-preview-panel">
            <div className="rounded-xl bg-slate-50 p-3 text-xs">
              <p className="text-slate-500 mb-1">Tenant: <strong className="text-slate-700">{wipePreview.tenant.name}</strong> <span className="font-mono text-slate-400">({wipePreview.tenant.slug})</span></p>
              <p className="text-slate-500">Docs to preserve: <strong className="text-green-700">{wipePreview.totals.preserved_docs}</strong> · Docs to wipe: <strong className="text-red-700">{wipePreview.totals.wiped_docs}</strong></p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg border border-green-200 bg-green-50 p-2">
                <p className="text-[10px] uppercase tracking-wider text-green-700 font-bold mb-1">Preserved</p>
                {Object.entries(wipePreview.preserved).length === 0 ? (
                  <p className="text-[11px] text-green-700/70">(none)</p>
                ) : (
                  Object.entries(wipePreview.preserved).map(([name, cnt]) => (
                    <p key={name} className="text-[11px] text-green-800 flex justify-between gap-2">
                      <span className="font-mono truncate">{name}</span>
                      <span className="font-bold">{cnt}</span>
                    </p>
                  ))
                )}
              </div>
              <div className="rounded-lg border border-red-200 bg-red-50 p-2 max-h-48 overflow-auto">
                <p className="text-[10px] uppercase tracking-wider text-red-700 font-bold mb-1">Will be wiped</p>
                {Object.entries(wipePreview.wiped).length === 0 ? (
                  <p className="text-[11px] text-red-700/70">(nothing to wipe)</p>
                ) : (
                  Object.entries(wipePreview.wiped).map(([name, cnt]) => (
                    <p key={name} className="text-[11px] text-red-800 flex justify-between gap-2">
                      <span className="font-mono truncate">{name}</span>
                      <span className="font-bold">{cnt}</span>
                    </p>
                  ))
                )}
              </div>
            </div>

            <div className="rounded-xl border-2 border-red-200 bg-red-50/60 p-3 space-y-2">
              <p className="text-xs text-red-700 font-bold">To confirm, type the tenant slug <code className="bg-white px-1 rounded font-mono">{wipePreview.tenant.slug}</code>:</p>
              <input
                value={wipeSlugConfirm}
                onChange={e => setWipeSlugConfirm(e.target.value)}
                placeholder={wipePreview.tenant.slug}
                className="w-full px-3 py-2 rounded-lg border text-sm font-mono"
                data-testid="wipe-slug-input"
              />
              <textarea
                value={wipeReason}
                onChange={e => setWipeReason(e.target.value)}
                rows={2}
                placeholder="Reason (>= 12 chars, audit-logged) — e.g. clean-slate re-launch for Diwali 2026 rollout"
                className="w-full px-3 py-2 rounded-lg border text-sm"
                data-testid="wipe-reason-input"
              />
              <button
                onClick={handleWipeExecute}
                disabled={wipeLoading || wipeSlugConfirm !== wipePreview.tenant.slug || !wipeReason || wipeReason.trim().length < 12}
                className="w-full py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-sm disabled:opacity-50"
                data-testid="wipe-execute-btn"
              >
                {wipeLoading ? "Wiping…" : `Wipe ${wipePreview.totals.wiped_docs} documents`}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Duplicate account panel */}
      {dupData && (
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-4" data-testid="dupe-panel">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-amber-600" />
            <h3 className="font-bold text-sm text-[#1e3a5f]">
              Accounts for {dupData.email} · {dupData.tenant.name}
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
            <div className={`rounded-lg p-2 ${dupData.counts.members_active_approved > 1 ? "bg-red-50 text-red-700" : "bg-slate-50 text-slate-600"}`}>
              <p className="font-bold">{dupData.counts.members_active_approved}</p>
              <p>active + approved members</p>
            </div>
            <div className={`rounded-lg p-2 ${dupData.counts.guests_active_verified > 1 ? "bg-red-50 text-red-700" : "bg-slate-50 text-slate-600"}`}>
              <p className="font-bold">{dupData.counts.guests_active_verified}</p>
              <p>active verified guests</p>
            </div>
          </div>
          {!dupData.unique_email_index_present && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-700 mb-3">
              iter130 partial unique index NOT present — historical duplicates block index creation until this dedupe runs.
            </div>
          )}
          {/* Members */}
          <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Members</p>
          <div className="space-y-2 mb-3">
            {dupData.members.length === 0 && <p className="text-slate-400 text-xs">No member rows.</p>}
            {dupData.members.map(m => (
              <div key={m.member_id || m.id} className="rounded-xl border border-slate-200 p-3 text-xs" data-testid={`dupe-member-${m.member_id || m.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-bold text-[#1e3a5f]">{m.name || "—"} <span className="text-slate-400 font-mono">{m.member_id || `id:${m.id?.slice(0,8)}…`}</span></p>
                    <p className="text-slate-500">
                      {m.approval_status} · {m.is_active ? "active" : "inactive"} · mode: {m.subscription_mode || "—"} · pw: {m.has_password ? "yes" : "no"}
                    </p>
                    <p className="text-slate-500">
                      created {new Date(m.created_at).toLocaleString()} {m.approved_at ? `· approved ${new Date(m.approved_at).toLocaleDateString()}` : ""}
                    </p>
                    {m.wallet && <p className="text-slate-500">wallet €{m.wallet.total_balance.toFixed(2)}</p>}
                    {m.stripe_subscription_id && <p className="text-slate-400 font-mono truncate">sub {m.stripe_subscription_id}</p>}
                  </div>
                  {m.is_active && (
                    <button
                      onClick={() => setDedupeConfirm({ kind: "member", record_id: m.member_id || m.id, name: m.name, balance: m.wallet?.total_balance || 0 })}
                      className="flex-shrink-0 px-2.5 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-[11px] font-bold flex items-center gap-1"
                      data-testid={`dupe-deactivate-${m.member_id || m.id}`}
                    >
                      <Power className="w-3 h-3" /> Deactivate
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {/* Guests */}
          <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Guests</p>
          <div className="space-y-2">
            {dupData.guests.length === 0 && <p className="text-slate-400 text-xs">No guest rows.</p>}
            {dupData.guests.map(g => (
              <div key={g.guest_id || g.id} className="rounded-xl border border-slate-200 p-3 text-xs" data-testid={`dupe-guest-${g.guest_id || g.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-bold text-[#1e3a5f]">{g.name || "—"} <span className="text-slate-400 font-mono">{g.guest_id || `id:${g.id?.slice(0,8)}…`}</span></p>
                    <p className="text-slate-500">
                      {g.is_active ? "active" : "inactive"} · {g.is_verified ? "verified" : "unverified"} {g.google_linked ? "· Google" : ""} · {g.created_via || "—"}
                    </p>
                    <p className="text-slate-500">created {new Date(g.created_at).toLocaleString()}</p>
                    {g.wallet && <p className="text-slate-500">wallet €{g.wallet.total_balance.toFixed(2)}</p>}
                  </div>
                  {g.is_active && (
                    <button
                      onClick={() => setDedupeConfirm({ kind: "guest", record_id: g.guest_id || g.id, name: g.name, balance: g.wallet?.total_balance || 0 })}
                      className="flex-shrink-0 px-2.5 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-[11px] font-bold flex items-center gap-1"
                      data-testid={`dupe-deactivate-${g.guest_id || g.id}`}
                    >
                      <Power className="w-3 h-3" /> Deactivate
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Export Result */}
      {exportData && (
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-sm text-[#1e3a5f]">Exported Data: {exportData.email}</h3>
            <button
              onClick={() => downloadJson(exportData, `gdpr-export-${exportData.email}-${Date.now()}.json`)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#1e3a5f] text-white text-xs font-medium"
            >
              <Download className="w-3 h-3" /> Download JSON
            </button>
          </div>
          <p className="text-xs text-slate-500 mb-2">Tenant: {exportData.tenant}</p>
          <div className="bg-slate-50 rounded-xl p-3 max-h-64 overflow-auto">
            <pre className="text-[10px] text-slate-600 whitespace-pre-wrap">{JSON.stringify(exportData.data, null, 2)}</pre>
          </div>
        </div>
      )}

      {/* Audit Log */}
      <div className="bg-white rounded-2xl p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-slate-400" />
          <h3 className="font-bold text-sm text-[#1e3a5f]">GDPR Audit Log</h3>
        </div>
        {auditLogs.length === 0 ? (
          <p className="text-slate-400 text-sm text-center py-4">No GDPR operations recorded</p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-auto">
            {auditLogs.map((log, i) => (
              <div key={log.id || i} className="bg-slate-50 rounded-xl p-3 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className={`px-2 py-0.5 rounded-full font-bold ${log.action === "data_deletion" ? "bg-red-100 text-red-600" : "bg-blue-100 text-blue-600"}`}>
                    {log.action === "data_deletion" ? "DELETION" : log.action?.toUpperCase()}
                  </span>
                  <span className="text-slate-400">{new Date(log.performed_at).toLocaleString()}</span>
                </div>
                <p className="text-slate-600"><strong>Email:</strong> {log.target_email}</p>
                <p className="text-slate-600"><strong>By:</strong> {log.performed_by}</p>
                {log.deleted_counts && (
                  <p className="text-slate-500 mt-1">
                    Deleted: {Object.entries(log.deleted_counts).filter(([,v]) => v > 0).map(([k,v]) => `${k}: ${v}`).join(", ") || "none"}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Permanent Data Deletion
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>This will <strong>permanently delete ALL data</strong> for:</p>
              <p className="bg-red-50 rounded-lg p-2 font-mono text-sm">{deleteConfirm?.email}</p>
              <p>From tenant: <strong>{deleteConfirm?.tenantName}</strong></p>
              <p className="text-red-500 font-medium">This action cannot be undone. Member records, wallet data, transactions, and all associated data will be permanently removed.</p>
              <p className="text-slate-500 text-xs">Audit-log entries referencing this person are <strong>retained</strong> under legitimate-interest lawful basis, but the personal identifiers are replaced with a stable pseudonym so the natural person can no longer be identified.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Delete All Data</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Duplicate-account deactivation dialog */}
      <AlertDialog open={!!dedupeConfirm} onOpenChange={(open) => { if (!open) { setDedupeConfirm(null); setDedupeReason(""); setDedupeForfeit(false); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Power className="w-5 h-5" /> Deactivate duplicate account
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Deactivate <strong>{dedupeConfirm?.kind}</strong>:</p>
              <p className="bg-slate-100 rounded-lg p-2 font-mono text-sm">
                {dedupeConfirm?.name || "—"} · {dedupeConfirm?.record_id}
              </p>
              <p className="text-slate-500 text-xs">
                The row's session is invalidated so any stale JWT is refused with 401 on next call.
                The action is logged to the GDPR audit trail below.
              </p>
              {dedupeConfirm?.balance > 0 && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-2 text-xs text-red-700">
                  This account still has €{dedupeConfirm.balance.toFixed(2)} wallet balance. Refund or transfer it first, or check "forfeit balance" below.
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 my-2">
            <label className="text-xs text-slate-500 block">Reason (required, min 8 chars — will be audit-logged)</label>
            <textarea
              value={dedupeReason}
              onChange={e => setDedupeReason(e.target.value)}
              placeholder="e.g. duplicate signup from Feb 2026; keeping MM-XYZ which owns the active Stripe subscription"
              rows={2}
              className="w-full px-3 py-2 rounded-xl border text-sm"
              data-testid="dedupe-reason-input"
            />
            {dedupeConfirm?.balance > 0 && (
              <label className="flex items-center gap-2 text-xs text-red-700">
                <input
                  type="checkbox"
                  checked={dedupeForfeit}
                  onChange={e => setDedupeForfeit(e.target.checked)}
                  data-testid="dedupe-forfeit-check"
                />
                Forfeit €{dedupeConfirm.balance.toFixed(2)} wallet balance (I confirm this is legacy dust)
              </label>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDedupe}
              disabled={dupLoading || !dedupeReason || dedupeReason.trim().length < 8 || (dedupeConfirm?.balance > 0 && !dedupeForfeit)}
              className="bg-red-500 hover:bg-red-600"
              data-testid="dedupe-confirm-btn"
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
